/**
 * RodeoEventDialog — Shared add/edit modal for rodeo events & event configs.
 *
 * Uses the shared WizardShell inside the Dialog so the add/edit UX mirrors
 * the jackpot EditEventDialog: left-nav steps (Event Details + Rounds),
 * Previous/Next in the footer, Cancel + save action on the right.
 *
 * Two callers today:
 *   - RodeoWizard Step 3 (EventConfigStep): writes to the zustand store
 *     (addEvent / updateEvent).
 *   - DelegateRodeosPage expanded list (PerformanceSection > EventRow):
 *     writes to the events table via CRUD hooks. Those paths don't
 *     round-trip rounds, so they pass `showRounds={false}` to keep the
 *     Rounds step hidden.
 *
 * The dialog is dumb — it owns form state and fires onSubmit(values). All
 * persistence decisions (API vs store, which fields map where) live in the
 * caller.
 */

import * as React from 'react';
import { useEffect, useState } from 'react';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { isTeamRopingEvent } from '@/utils/eventTypes';
import { describeBalancedSplit } from '@/utils/balancedPositions';
import { WizardShell, type WizardStepDef } from '@/components/wizard';
import { RODEO_EVENT_TYPE_NAMES, findRodeoEventType } from '@/constants/event-types';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatDate, formatTime } from '@/utils/formatters';
import { useStripeFeeConfig } from '@/api/extensions/useStripeFeeConfig';
import { calculateTransactionFee } from '@/utils/stripe-fee-calculator';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { useJudgeSheetTemplates } from '@/api/extensions/useJudgeSheetTemplates';
import { useOrgDefaultPrizeAdjustments, useOrgPrizeSettings } from '@/api/extensions/usePrizeOps';

/**
 * Round config shape for the dialog. Mirrors RoundConfigDraft but without
 * the client-side `_key` (the dialog manages keys internally and emits a
 * fresh list on save).
 *
 * Prize-related fields are stored as metadata-backed extras so the dialog
 * can mirror the jackpot EditEventDialog's per-round prize allocation.
 */
export interface RodeoEventDialogRound {
  round_number: number;
  round_name: string;
  is_go_round: boolean;
  is_short_round: boolean;
  advancing_count: number | null;
  /**
   * Average-of-all-rounds payout row. Exactly one per event; cannot
   * be deleted from the UI (the dialog hides its delete button, same
   * rule as Round 1). Persisted via `metadata.is_average` on
   * rodeo_round_configs. Performance assignment is NULL — the Avg
   * row is a payout aggregate, not a run.
   */
  is_average?: boolean;
  /**
   * Performance this round runs in. Opaque key drawn from the SAME key space
   * as `RodeoEventDialogPerformanceAssignment.performance_key` (a perf draft
   * `_key` in the wizard, a real `performances.id` on the persisted surfaces),
   * so the dialog can only ever point a round at a perf this event is
   * actually assigned to. `null` means "Default" — the caller falls back to
   * the event's first assigned performance. Always null for the Avg row.
   */
  performance_key?: string | null;
  // Prize config (jackpot-parity)
  has_prizes: boolean;
  round_prize_total: number;
  is_progressive: boolean;
  places: RodeoEventDialogPrizePlace[];
}

export interface RodeoEventDialogPrizePlace {
  place: number;
  percentage: number;
}

export interface RodeoEventDialogPrizeAdjustment {
  id: string;
  operation: 'add' | 'subtract';
  adjustment_type: 'amount' | 'percentage';
  amount: number;
  percentage: number;
  reason: string;
}

/**
 * Per-performance assignment for an event. Each entry says "this event runs in
 * this performance, and (optionally) caps at this many contestants for that
 * specific perf". NULL/undefined max_contestants inherits the event's
 * rodeo-level contestant_limit.
 */
export interface RodeoEventDialogPerformanceAssignment {
  /** Opaque key — caller decides if this is a performance draft _key or a real id. */
  performance_key: string;
  /** Per-perf cap override. NULL means use the event's contestant_limit. */
  max_contestants: number | null;
}

export interface RodeoEventDialogValues {
  /**
   * Optional custom display name. When blank, downstream surfaces fall back
   * to `event_type` via the shared `getEventDisplayTitle` resolver.
   */
  event_name: string;
  event_type: string;
  event_category: 'timed' | 'scored';
  entry_fee: number | null;
  transaction_fee: number | null;
  contestant_limit: number | null;
  /**
   * Default number of contestants that will run in any single performance.
   * Persists to `events.contestants_per_performance`. NULL means no global
   * default — the draw falls back to splitting `contestant_limit` across
   * the assigned performances. Per-performance overrides still ride on
   * `performance_assignments[i].max_contestants`.
   */
  contestants_per_performance: number | null;
  non_member_surcharge: number | null;
  /**
   * When true, the same contestant may register more than once for this
   * event up to `max_registrations_per_user`. Defaults to false (one
   * entry per contestant; team roping still gets the implicit
   * Header+Heeler exception via position). Optional so existing callers
   * that don't set it still pass type checks — the dialog folds it back
   * to `false` via the EMPTY constant before rendering.
   */
  allow_multiple_registrations?: boolean;
  /**
   * Per-contestant entry cap when multi-registration is enabled. Ignored
   * when `allow_multiple_registrations` is false. Defaults to 1.
   */
  max_registrations_per_user?: number | null;
  /**
   * Team roping only. When true, a contestant's entries for this event must
   * split as evenly as possible between Header and Heeler — at most
   * ceil(max_registrations_per_user / 2) per side. Ignored when the cap is 1
   * or the discipline is solo. Defaults to false.
   */
  enforce_balanced_positions?: boolean;
  /**
   * Per-event override of the rodeo's Pay Later switch. `null`/undefined
   * inherits the rodeo; an explicit boolean wins in either direction.
   */
  allow_pay_later?: boolean | null;
  /**
   * Performances this event runs in. Empty array is valid — the event can be
   * defined without being scheduled yet. Callers that only want one perf can
   * still pass a single-element array.
   */
  performance_assignments: RodeoEventDialogPerformanceAssignment[];
  /** Ordered list of rounds attached to this event. */
  rounds: RodeoEventDialogRound[];
  // Prize management (jackpot-parity, persisted via metadata jsonb)
  total_prize_amount: number | null;
  prize_adjustments: RodeoEventDialogPrizeAdjustment[];
  /**
   * How the prize pool is determined. 'fixed' uses total_prize_amount;
   * 'dynamic' computes dynamic_prize_per_entry × number of entries live.
   * Persists to events.prize_allocation_mode. Optional for back-compat —
   * folds to 'fixed' via EMPTY.
   */
  prize_allocation_mode?: 'fixed' | 'dynamic';
  /**
   * Per-entry contribution to the prize pool (dynamic mode only): a flat dollar
   * amount per entry, a percentage of the entry fee when
   * entry_fee_prize_portion_is_percentage is true, or a dollar amount deducted
   * from the entry fee (contribution = entry_fee − value) when
   * entry_fee_prize_portion_is_fee_deduction is true. events.entry_fee_prize_portion.
   */
  entry_fee_prize_portion?: number | null;
  entry_fee_prize_portion_is_percentage?: boolean;
  entry_fee_prize_portion_is_fee_deduction?: boolean;
  /** Show the (live) prize to contestants. events.display_prize_to_contestants. */
  display_prize_to_contestants?: boolean;
  /**
   * Show prize amounts in results before every contestant has been scored.
   * events.show_prize_amounts_before_complete. Default false: the results
   * prize column stays hidden until the event is complete.
   */
  show_prize_amounts_before_complete?: boolean;
  /**
   * Master toggle: whether prize allocations are shown next to placed
   * contestants in results at all. events.show_prize_amounts_in_results.
   * Defaults true. When false, prize amounts never appear in results and
   * show_prize_amounts_before_complete has no effect.
   */
  show_prize_amounts_in_results?: boolean;
  /**
   * Per-event overrides of inherited org-default prize adjustments, keyed by
   * the org-default id. Absent keys inherit the org default (active, default
   * amount). events.default_adjustment_overrides.
   */
  default_adjustment_overrides?: Record<
    string,
    { is_active?: boolean; amount?: number | null; percentage?: number | null }
  >;
  /**
   * FK to public.judge_sheet_templates. NULL = auto-classify by discipline
   * (the trigger picks barrel_racing / rough_stock / timed from the event
   * type name). A non-null id pins the event to a specific template.
   */
  judge_sheet_template_id?: string | null;
}

export interface RodeoEventDialogPerformanceOption {
  key: string;
  name: string;
  /** Perf date (YYYY-MM-DD). Rendered as a human-readable date. */
  performance_date?: string | null;
  /** Perf start time (HH:MM[:SS]). Rendered in 12-hour format. */
  start_time?: string | null;
  /**
   * Event types already claiming this performance (excluding the event
   * currently being edited). Shown as chips next to the toggle so the
   * user knows picking this perf will conflict with / steal from those
   * events. Empty list means the perf is unclaimed.
   */
  assigned_event_types?: string[];
  /**
   * Perf-level default cap set on the performance itself. Used as a fallback
   * when the event config doesn't override it via the junction — shown in
   * the cap input's placeholder so the user sees the effective cap without
   * having to re-type it.
   */
  default_max_contestants?: number | null;
}

export interface RodeoEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'add' | 'edit';
  initialValues?: Partial<RodeoEventDialogValues>;
  onSubmit: (values: RodeoEventDialogValues) => void | Promise<void>;
  /** Dropdown options for the "Assigned Performance" select. */
  performances?: RodeoEventDialogPerformanceOption[];
  errorMessage?: string | null;
  isSaving?: boolean;
  /**
   * Show the inline rounds editor as its own step. Defaults to true (the
   * wizard folds rounds into the event dialog). Callers that don't persist
   * rounds from their save path set this to false to collapse the nav to
   * a single "Event Details" step.
   */
  showRounds?: boolean;
  /**
   * Step to open on (1-based, matching the left-nav order:
   * 1 = Event Details, 2 = Rounds, 3 = Prizes). Defaults to 1.
   *
   * Lets a caller that has a prize-specific entry point — the Prize Management
   * tab's "Edit prizes" button — drop the user straight on the Prizes step
   * instead of making them click through Event Details and Rounds to reach the
   * thing they asked for. Clamped to the steps actually rendered, so passing 3
   * alongside showRounds={false} lands on Event Details rather than a step that
   * doesn't exist.
   */
  initialStep?: number;
  /**
   * Event types already in use by OTHER events in this rodeo. Since the
   * refactor to one-config-per-event-type, uniqueness is rodeo-wide, not
   * per-perf. Case-insensitive. The dialog never disables the event's own
   * current type — editing a Barrel Racing event still lets the user keep
   * "Barrel Racing" selected.
   */
  usedEventTypes?: string[];
  /**
   * Org that owns this rodeo. When set, the dialog fetches that org's default
   * prize adjustments and renders them in the Prize Adjustments section as
   * inherited, toggleable entries (locked wording, editable amount, no delete).
   * Per-event overrides ride in `default_adjustment_overrides`.
   */
  organizationId?: string | null;
}

/**
 * Event discipline options shown in the rodeo wizard's event-type select.
 * Delegates to the canonical list so the wizard stays in sync with the
 * jackpot EditEventDialog and the `events.trig_event_type_name` column.
 */
export const COMMON_EVENT_TYPES = [...RODEO_EVENT_TYPE_NAMES];

const PLACE_LABELS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];

/**
 * Radix Select forbids an empty-string item value, so "no explicit
 * performance" rides as a sentinel and maps back to null on change.
 */
const DEFAULT_PERF_VALUE = '__default__';

/**
 * Order performances by start datetime so every list in this dialog (the
 * Event Details toggles and the per-round picker) shows them chronologically
 * regardless of how the parent surface sorted them. Missing date/time sinks
 * to the bottom; name is the tie-break so the order stays stable.
 */
function byPerformanceStart(
  a: RodeoEventDialogPerformanceOption,
  b: RodeoEventDialogPerformanceOption,
): number {
  const aKey = `${a.performance_date ?? '9999-12-31'} ${a.start_time ?? '23:59:59'}`;
  const bKey = `${b.performance_date ?? '9999-12-31'} ${b.start_time ?? '23:59:59'}`;
  if (aKey !== bKey) return aKey < bKey ? -1 : 1;
  return (a.name ?? '').localeCompare(b.name ?? '');
}

/** Standard payout distributions by place count. */
const DEFAULT_DISTRIBUTIONS: Record<number, number[]> = {
  1: [100],
  2: [60, 40],
  3: [50, 30, 20],
  4: [40, 30, 20, 10],
  5: [35, 25, 20, 12, 8],
  6: [30, 25, 20, 15, 7, 3],
  7: [25, 20, 18, 15, 12, 7, 3],
  8: [22, 18, 16, 14, 12, 8, 6, 4],
  9: [20, 16, 14, 12, 10, 8, 6, 4, 2],
  10: [18, 15, 13, 11, 9, 7, 5, 4, 3, 2],
};

function createDefaultPlaces(count: number): RodeoEventDialogPrizePlace[] {
  const pcts =
    DEFAULT_DISTRIBUTIONS[count] ??
    Array.from({ length: count }, () => Math.floor(100 / count));
  return pcts.map((p, i) => ({ place: i + 1, percentage: p }));
}

const EMPTY: RodeoEventDialogValues = {
  event_name: '',
  event_type: '',
  event_category: 'timed',
  entry_fee: null,
  transaction_fee: null,
  contestant_limit: null,
  contestants_per_performance: null,
  non_member_surcharge: null,
  allow_multiple_registrations: false,
  max_registrations_per_user: null,
  enforce_balanced_positions: false,
  allow_pay_later: null,
  performance_assignments: [],
  rounds: [],
  total_prize_amount: null,
  prize_adjustments: [],
  prize_allocation_mode: 'fixed',
  entry_fee_prize_portion: null,
  entry_fee_prize_portion_is_percentage: false,
  entry_fee_prize_portion_is_fee_deduction: false,
  display_prize_to_contestants: false,
  show_prize_amounts_before_complete: false,
  show_prize_amounts_in_results: true,
  default_adjustment_overrides: {},
  judge_sheet_template_id: null,
};

export function RodeoEventDialog({
  open,
  onOpenChange,
  mode,
  initialValues,
  onSubmit,
  performances,
  errorMessage,
  isSaving,
  showRounds = true,
  initialStep = 1,
  usedEventTypes,
  organizationId,
}: RodeoEventDialogProps) {
  const [type, setType] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<'timed' | 'scored'>('timed');
  const [entryFee, setEntryFee] = useState('');
  const [regFee, setRegFee] = useState('');
  const [limit, setLimit] = useState('');
  const [perPerfLimit, setPerPerfLimit] = useState('');
  const [surcharge, setSurcharge] = useState('');
  const [allowMultiReg, setAllowMultiReg] = useState(false);
  const [maxPerUser, setMaxPerUser] = useState('');
  const [balancedPositions, setBalancedPositions] = useState(false);
  // Tri-state picker value; 'inherit' maps to null on the wire.
  const [payLaterOverride, setPayLaterOverride] = useState<
    'inherit' | 'allow' | 'disallow'
  >('inherit');
  const [assignments, setAssignments] = useState<RodeoEventDialogPerformanceAssignment[]>([]);
  const [rounds, setRounds] = useState<RodeoEventDialogRound[]>([]);
  const [totalPrize, setTotalPrize] = useState('');
  const [adjustments, setAdjustments] = useState<RodeoEventDialogPrizeAdjustment[]>([]);
  // Dynamic prize allocation + contestant display settings.
  const [prizeMode, setPrizeMode] = useState<'fixed' | 'dynamic'>('fixed');
  // Single per-entry contribution (dynamic only): value + $/% type.
  const [perEntry, setPerEntry] = useState('');
  // How the per-entry value is interpreted: a flat dollar amount, a % of the
  // entry fee, or a dollar amount DEDUCTED from the entry fee (the rest goes
  // to the prize). 'fee_deduction' lets an org "put the whole entry fee in
  // minus a fixed cut they keep".
  const [perEntryMode, setPerEntryMode] = useState<'amount' | 'percentage' | 'fee_deduction'>(
    'amount',
  );
  const [displayPrize, setDisplayPrize] = useState(false);
  // Master toggle (defaults ON): whether prize allocations appear in results.
  const [showPrizeInResults, setShowPrizeInResults] = useState(true);
  const [showPrizeBeforeComplete, setShowPrizeBeforeComplete] = useState(false);
  // Org default per-entry contribution — pre-fills a NEW event's per-entry
  // field when the organizer hasn't set one yet.
  const { data: orgPrizeSettings } = useOrgPrizeSettings(organizationId);
  // Inherited org-default prize adjustments + per-event overrides (toggle /
  // amount). Wording is locked; defaults can't be deleted at the event level.
  const { data: orgDefaults } = useOrgDefaultPrizeAdjustments(organizationId);
  const [defaultOverrides, setDefaultOverrides] = useState<
    Record<string, { is_active?: boolean; amount?: number | null; percentage?: number | null }>
  >({});
  // Per-event judge-sheet template override. null = auto-classify by discipline.
  const [judgeSheetTemplateId, setJudgeSheetTemplateId] = useState<string | null>(null);
  const { data: judgeSheetTemplates } = useJudgeSheetTemplates();
  const [step, setStep] = useState<number>(1);
  // Super-admin-only override toggle for the Stripe-derived transaction fee.
  // Default closed on every open — admins must opt in to override.
  const [editTxnFee, setEditTxnFee] = useState(false);

  const { data: stripeFeeConfig } = useStripeFeeConfig();
  const isAllOrgAdmin = useAuthStore((s) => s.isAllOrgAdmin)();

  useEffect(() => {
    if (!open) return;
    const iv = { ...EMPTY, ...(initialValues ?? {}) };
    const loadedType = iv.event_type ?? '';
    setType(loadedType);
    setName(iv.event_name ?? '');
    // Category is derived from the canonical event-type list; fall back to the
    // stored value (or 'timed') for non-canonical / 'Other' / empty cases.
    setCategory(findRodeoEventType(loadedType)?.category ?? iv.event_category ?? 'timed');
    setEntryFee(iv.entry_fee != null ? String(iv.entry_fee) : '');
    // Treat a stored 0 (or null) the same as "no override" so the dialog
    // shows the live Stripe-derived fee on reopen. A 0 row originates from
    // legacy data or from prior "Reset to auto" saves that pre-date the
    // null-preserving submit fix; surfacing it as an override would force
    // the readonly field to render $0.00 even though the backend will
    // still charge the dynamic Stripe fee at registration time.
    setRegFee(
      iv.transaction_fee != null && iv.transaction_fee > 0
        ? String(iv.transaction_fee)
        : '',
    );
    // Treat a stored 0 the same as null/empty — the column's semantics are
    // "positive integer = hard cap, anything else = unlimited", and the
    // capacity check on the backend treats 0 as "always full". Surfacing
    // "0" in the input would let the user save it back unchanged and
    // accidentally block all registrations.
    setLimit(
      iv.contestant_limit != null && iv.contestant_limit > 0
        ? String(iv.contestant_limit)
        : '',
    );
    // Same coercion as `limit`: treat 0/null/negative as "no override" so
    // legacy / blanked values render the input as empty rather than "0".
    setPerPerfLimit(
      iv.contestants_per_performance != null && iv.contestants_per_performance > 0
        ? String(iv.contestants_per_performance)
        : '',
    );
    // 0 means "use rodeo default" (matches the placeholder copy and the
    // null-on-blank semantics of the surcharge metadata field). Surfacing
    // "0" in the input would let the user save it back unchanged and read
    // like an explicit $0 override, which is misleading.
    setSurcharge(
      typeof iv.non_member_surcharge === 'number' && iv.non_member_surcharge > 0
        ? String(iv.non_member_surcharge)
        : '',
    );
    setAllowMultiReg(!!iv.allow_multiple_registrations);
    setMaxPerUser(
      iv.max_registrations_per_user != null
        ? String(iv.max_registrations_per_user)
        : '',
    );
    setBalancedPositions(!!iv.enforce_balanced_positions);
    setPayLaterOverride(
      typeof iv.allow_pay_later === 'boolean'
        ? iv.allow_pay_later
          ? 'allow'
          : 'disallow'
        : 'inherit',
    );
    setAssignments(
      Array.isArray(iv.performance_assignments)
        ? iv.performance_assignments.map((a) => ({
            performance_key: a.performance_key,
            max_contestants: a.max_contestants ?? null,
          }))
        : [],
    );
    const loadedRounds = Array.isArray(iv.rounds)
      ? iv.rounds.map((r) => ({
          ...r,
          is_average: !!r.is_average,
          // The Avg row is a payout aggregate, not a run — it never carries a
          // performance, regardless of what a legacy row stored.
          performance_key: r.is_average ? null : (r.performance_key ?? null),
          has_prizes: !!r.has_prizes,
          round_prize_total: Number(r.round_prize_total) || 0,
          is_progressive: !!r.is_progressive,
          places: Array.isArray(r.places) ? r.places.map((p) => ({ ...p })) : [],
        }))
      : [];
    // Setup floor: every event must always render at least one editable
    // round. The Avg row is scoring-only and intentionally not shown in
    // setup — when an existing event has only an Avg row stored (or no
    // rounds at all), prepend Round 1 so the user always sees one
    // non-removable round to edit. Existing Avg rows stay in state so
    // they round-trip through save and remain available to the scoring
    // layer; the rounds-editor and prize-allocation panels filter them
    // out for display only.
    const hasNonAvg = loadedRounds.some((r) => !r.is_average);
    const effectiveRounds: RodeoEventDialogRound[] = hasNonAvg
      ? loadedRounds
      : [
          {
            round_number: 1,
            round_name: '',
            is_go_round: true,
            is_short_round: false,
            advancing_count: null,
            is_average: false,
            performance_key: null,
            has_prizes: false,
            round_prize_total: 0,
            is_progressive: false,
            places: [],
          },
          ...loadedRounds,
        ];
    setRounds(effectiveRounds);
    setTotalPrize(iv.total_prize_amount != null ? String(iv.total_prize_amount) : '');
    setPrizeMode(iv.prize_allocation_mode === 'dynamic' ? 'dynamic' : 'fixed');
    // Per-entry contribution: seed from the event's saved value. New events with
    // no value are pre-filled from the org default in a separate effect below
    // (so a late-loading org setting still applies).
    setPerEntry(
      iv.entry_fee_prize_portion != null && iv.entry_fee_prize_portion > 0
        ? String(iv.entry_fee_prize_portion)
        : '',
    );
    setPerEntryMode(
      iv.entry_fee_prize_portion_is_fee_deduction
        ? 'fee_deduction'
        : iv.entry_fee_prize_portion_is_percentage
          ? 'percentage'
          : 'amount',
    );
    setDisplayPrize(!!iv.display_prize_to_contestants);
    // Master toggle defaults ON when omitted (legacy events keep showing
    // amounts once scoring completes).
    setShowPrizeInResults(iv.show_prize_amounts_in_results !== false);
    setShowPrizeBeforeComplete(!!iv.show_prize_amounts_before_complete);
    setDefaultOverrides(
      iv.default_adjustment_overrides && typeof iv.default_adjustment_overrides === 'object'
        ? { ...iv.default_adjustment_overrides }
        : {},
    );
    setAdjustments(
      Array.isArray(iv.prize_adjustments)
        ? iv.prize_adjustments.map((a) => ({ ...a }))
        : [],
    );
    setJudgeSheetTemplateId(iv.judge_sheet_template_id ?? null);
    // Clamp: the nav collapses to a single step when showRounds is false, so an
    // out-of-range initialStep must fall back to Event Details, not strand the
    // user on a step with no content.
    const maxStep = showRounds ? 3 : 1;
    setStep(Math.min(Math.max(1, initialStep), maxStep));
    setEditTxnFee(false);
  }, [open, initialValues, mode, initialStep, showRounds]);

  // Pre-fill the per-entry contribution from the org default whenever dynamic
  // allocation is selected and the field is still empty — covers both a new
  // event and switching an existing (fixed) event to dynamic. Only fills while
  // empty, so it never clobbers a saved value or what the organizer typed.
  useEffect(() => {
    if (!open || prizeMode !== 'dynamic') return;
    if (perEntry !== '') return;
    if (orgPrizeSettings && orgPrizeSettings.default_entry_fee_prize_portion > 0) {
      setPerEntry(String(orgPrizeSettings.default_entry_fee_prize_portion));
      setPerEntryMode(
        orgPrizeSettings.default_entry_fee_prize_portion_is_fee_deduction
          ? 'fee_deduction'
          : orgPrizeSettings.default_entry_fee_prize_portion_is_percentage
            ? 'percentage'
            : 'amount',
      );
    }
  }, [open, prizeMode, orgPrizeSettings, perEntry]);

  // Live Stripe-derived transaction fee preview. Matches the backend
  // calculator so the value shown here is what the registrant will be
  // charged. Falls back to 0 while the platform fee config is loading.
  const entryFeeNum = entryFee ? parseFloat(entryFee) : 0;
  const autoTxnFee = stripeFeeConfig
    ? calculateTransactionFee(
        Number.isFinite(entryFeeNum) ? entryFeeNum : 0,
        stripeFeeConfig,
      )
    : 0;
  const hasManualOverride = regFee.trim() !== '';
  const displayedTxnFee = hasManualOverride
    ? parseFloat(regFee) || 0
    : autoTxnFee;

  const canSubmit = type.trim().length > 0 && !isSaving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit({
      event_name: name.trim(),
      event_type: type.trim(),
      event_category: category,
      entry_fee: entryFee ? parseFloat(entryFee) : null,
      transaction_fee: regFee ? parseFloat(regFee) : null,
      // Empty input → null (Unlimited). A typed "0" parses to 0, which is
      // semantically the same as Unlimited (and trips the capacity check
      // as "always full" if it ever lands in the events row), so collapse
      // non-positive values to null here too.
      contestant_limit: (() => {
        if (!limit) return null;
        const parsed = parseInt(limit, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      })(),
      // Mirror contestant_limit's "positive int or null" semantics — the
      // backend's positive-int CHECK rejects 0/negative, so collapse those
      // to null so a stale 0 round-trip never blocks save.
      contestants_per_performance: (() => {
        if (!perPerfLimit) return null;
        const parsed = parseInt(perPerfLimit, 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      })(),
      non_member_surcharge: surcharge ? parseFloat(surcharge) : null,
      allow_multiple_registrations: allowMultiReg,
      // When the toggle is off the cap field is moot — emit 1 (the DB
      // default, the CHECK constraint's lower bound) so callers never
      // round-trip null into the events row (where Fastify's validator
      // coerces it to 0 and trips events_max_registrations_per_user_check).
      // When on, clamp to [1,10] to match the CHECK constraint's upper
      // bound and default to 1 if the admin left the input empty.
      max_registrations_per_user: allowMultiReg
        ? maxPerUser
          ? Math.min(10, Math.max(1, parseInt(maxPerUser, 10) || 1))
          : 1
        : 1,
      // Only persist the balance rule when it can actually bite — a solo
      // discipline or a cap of 1 makes it a no-op server-side, and writing
      // a stale `true` would resurface the toggle if the event later
      // becomes multi-entry team roping.
      enforce_balanced_positions: balancedPositions && allowMultiReg,
      // null = inherit the rodeo's Pay Later switch. Always emitted so
      // switching back to "Inherit" clears a previously-saved override.
      allow_pay_later:
        payLaterOverride === 'inherit' ? null : payLaterOverride === 'allow',
      performance_assignments: assignments,
      rounds: rounds.map((r, i) => ({
        ...r,
        // Renumber on submit so deletes/reorders produce a clean 1..N sequence.
        round_number: i + 1,
      })),
      // Total Prize Amount is the fixed-mode base only; a dynamic event has no
      // flat base (its money comes from per-entry + adjustments).
      total_prize_amount: prizeMode === 'fixed' && totalPrize ? parseFloat(totalPrize) : 0,
      prize_adjustments: adjustments,
      prize_allocation_mode: prizeMode,
      // Per-entry contribution only applies in dynamic mode; persist 0 for fixed
      // so a stale value can't leak into the pool. Cap at the entry fee ($) /
      // 100% so we never add more to the prize per entry than we collect.
      entry_fee_prize_portion: (() => {
        if (prizeMode !== 'dynamic' || !perEntry) return 0;
        const n = Math.max(0, parseFloat(perEntry) || 0);
        if (perEntryMode === 'percentage') return Math.min(n, 100);
        // Flat amount and fee-deduction are both dollar values bounded by the
        // entry fee (a deduction ≥ the fee just yields a $0 contribution).
        return entryFeeNum > 0 ? Math.min(n, entryFeeNum) : n;
      })(),
      entry_fee_prize_portion_is_percentage:
        prizeMode === 'dynamic' && perEntryMode === 'percentage',
      entry_fee_prize_portion_is_fee_deduction:
        prizeMode === 'dynamic' && perEntryMode === 'fee_deduction',
      display_prize_to_contestants: displayPrize,
      show_prize_amounts_in_results: showPrizeInResults,
      // Pre-complete reveal only matters when the master toggle is on.
      show_prize_amounts_before_complete: showPrizeInResults && showPrizeBeforeComplete,
      default_adjustment_overrides: defaultOverrides,
      judge_sheet_template_id: judgeSheetTemplateId,
    });
  };

  const addRound = () => {
    setRounds((prev) => {
      // Keep the Avg row pinned to the end — insert new rounds
      // immediately before it. Also covers the no-Avg safety case,
      // though the effect above guarantees one exists.
      const avgIdx = prev.findIndex((r) => r.is_average);
      const insertAt = avgIdx === -1 ? prev.length : avgIdx;
      const newRound: RodeoEventDialogRound = {
        round_number: insertAt + 1,
        round_name: '',
        is_go_round: true,
        is_short_round: false,
        advancing_count: null,
        is_average: false,
        // New rounds start on "Default" (the event's first assigned perf).
        performance_key: null,
        has_prizes: false,
        round_prize_total: 0,
        is_progressive: false,
        places: [],
      };
      return [
        ...prev.slice(0, insertAt),
        newRound,
        ...prev.slice(insertAt),
      ];
    });
  };

  const updateRound = (index: number, patch: Partial<RodeoEventDialogRound>) => {
    setRounds((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
  };

  const removeRound = (index: number) => {
    setRounds((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Toggle a round's `has_prizes` and auto-split the available prize evenly
   * across the resulting set of enabled rounds. Meets the UX spec:
   *   - Enabling the only round → that round gets 100% of availablePrize.
   *   - Enabling an Nth round → all N enabled rounds split evenly.
   *   - Disabling a round → remaining enabled rounds split evenly; disabled
   *     round's total is zeroed out.
   * Any remainder after integer division lands on the last enabled round so
   * the sums match availablePrize exactly.
   */
  /**
   * Distribute the available prize pool evenly across every ENABLED prize
   * round — go-rounds AND the Average (all rounds) row. Splitting in cents
   * avoids float drift; the last enabled round absorbs the leftover penny.
   * Disabled rounds are zeroed; newly-enabled rounds get a default place split.
   */
  const redistributePrize = (
    rows: RodeoEventDialogRound[],
  ): RodeoEventDialogRound[] => {
    const pool = Math.max(
      0,
      Math.round((prizeBase + totalAdjustments) * 100) / 100,
    );
    const enabledIdxs = rows
      .map((r, i) => (r.has_prizes ? i : -1))
      .filter((i) => i !== -1);
    if (enabledIdxs.length === 0) {
      return rows.map((r) => (r.has_prizes ? r : { ...r, round_prize_total: 0 }));
    }
    const poolCents = Math.round(pool * 100);
    const shareCents = Math.floor(poolCents / enabledIdxs.length);
    const remainderCents = poolCents - shareCents * enabledIdxs.length;
    return rows.map((r, i) => {
      if (!r.has_prizes) return { ...r, round_prize_total: 0 };
      const orderInEnabled = enabledIdxs.indexOf(i);
      const isLast = orderInEnabled === enabledIdxs.length - 1;
      const cents = shareCents + (isLast ? remainderCents : 0);
      return {
        ...r,
        round_prize_total: cents / 100,
        places:
          r.places && r.places.length > 0 ? r.places : createDefaultPlaces(3),
      };
    });
  };

  /**
   * Enable/disable prizes for a go-round, then re-split the pool evenly across
   * all enabled prize rounds (go-rounds + Average).
   */
  const toggleRoundPrize = (index: number, enabled: boolean) => {
    setRounds((prev) =>
      redistributePrize(
        prev.map((r, i) => (i === index ? { ...r, has_prizes: enabled } : r)),
      ),
    );
  };

  /**
   * Enable/disable the Average (all rounds) payout. It now participates in the
   * same even-split auto-allocation as go-rounds, so enabling it pulls a share
   * of the pool (and re-splits the other enabled rounds). Stored as the single
   * `is_average` row in `rounds` (round-trips via
   * rodeo_round_configs.metadata.is_average); created lazily on first enable.
   */
  const toggleAvgPrize = (enabled: boolean) => {
    setRounds((prev) => {
      const idx = prev.findIndex((r) => r.is_average);
      let next: RodeoEventDialogRound[];
      if (idx === -1) {
        if (!enabled) return prev;
        next = [
          ...prev,
          {
            round_number: prev.length + 1,
            round_name: 'Average (All Rounds)',
            is_go_round: false,
            is_short_round: false,
            advancing_count: null,
            is_average: true,
            has_prizes: true,
            round_prize_total: 0,
            is_progressive: false,
            places: createDefaultPlaces(3),
          },
        ];
      } else {
        next = prev.map((r, i) =>
          i === idx
            ? {
                ...r,
                has_prizes: enabled,
                places:
                  enabled && (!r.places || r.places.length === 0)
                    ? createDefaultPlaces(3)
                    : r.places,
              }
            : r,
        );
      }
      return redistributePrize(next);
    });
  };

  const updateAvgRound = (patch: Partial<RodeoEventDialogRound>) => {
    setRounds((prev) => prev.map((r) => (r.is_average ? { ...r, ...patch } : r)));
  };

  const missingSteps = new Set<number>();
  if (type.trim().length === 0) missingSteps.add(1);

  const steps: WizardStepDef[] = showRounds
    ? [{ label: 'Event Details' }, { label: 'Rounds' }, { label: 'Prizes' }]
    : [{ label: 'Event Details' }];

  // ── Prize helpers ──────────────────────────────────────────────────────

  // Preview base: fixed mode uses the Total Prize Amount; dynamic mode has no
  // flat base (its money comes from per-entry × entries — unknown at setup, so
  // previewed as 0 — plus the adjustments below). The backend engine is the
  // authoritative live figure.
  const prizeBase = prizeMode === 'dynamic' ? 0 : Number(totalPrize) || 0;
  // Dollars first, then percentages off the resulting subtotal — mirrors the
  // shared engine (calculateEventPrize): a "−4%" is taken after every dollar
  // addition/deduction has landed. Custom rows and inherited org-default rows
  // (with any per-event amount/percentage override) draw from the same pool.
  const dollarDelta = (() => {
    const custom = adjustments.reduce(
      (sum, a) =>
        a.adjustment_type === 'amount'
          ? sum + (a.operation === 'add' ? a.amount : -a.amount)
          : sum,
      0,
    );
    const defaults = (orgDefaults ?? []).reduce((sum, d) => {
      const ov = defaultOverrides[d.id] ?? {};
      if (!(ov.is_active ?? d.is_active)) return sum;
      if (d.percentage !== null && d.percentage !== undefined) return sum;
      const val = ov.amount ?? d.amount;
      return sum + (d.adjustment_type === 'addition' ? val : -val);
    }, 0);
    return custom + defaults;
  })();
  const prizeSubtotal = prizeBase + dollarDelta;
  const percentageDelta = (() => {
    const custom = adjustments.reduce((sum, a) => {
      if (a.adjustment_type === 'amount') return sum;
      const val = (prizeSubtotal * (a.percentage || 0)) / 100;
      return sum + (a.operation === 'add' ? val : -val);
    }, 0);
    const defaults = (orgDefaults ?? []).reduce((sum, d) => {
      const ov = defaultOverrides[d.id] ?? {};
      if (!(ov.is_active ?? d.is_active)) return sum;
      if (d.percentage === null || d.percentage === undefined) return sum;
      const val = (prizeSubtotal * ((ov.percentage ?? d.percentage) || 0)) / 100;
      return sum + (d.adjustment_type === 'addition' ? val : -val);
    }, 0);
    return custom + defaults;
  })();
  const totalAdjustments = dollarDelta + percentageDelta;
  const availablePrize = prizeBase + totalAdjustments;
  // Every payout draws from the same pool — go-rounds AND the Average (all
  // rounds) allocation, which is configured as its own card below. Count them
  // all so the over/under indicator reflects the real total committed.
  const totalAllocated = rounds.reduce(
    (sum, r) => sum + (r.has_prizes ? Number(r.round_prize_total) || 0 : 0),
    0,
  );
  const avgRound = rounds.find((r) => r.is_average) ?? null;
  const remainingPrize = availablePrize - totalAllocated;

  const [newAdjustment, setNewAdjustment] = useState<{
    operation: 'add' | 'subtract';
    adjustment_type: 'amount' | 'percentage';
    amount: string;
    percentage: string;
    reason: string;
    show: boolean;
  }>({
    operation: 'add',
    adjustment_type: 'amount',
    amount: '',
    percentage: '',
    reason: '',
    show: false,
  });

  const saveAdjustment = () => {
    if (!newAdjustment.reason.trim()) return;
    const amt = parseFloat(newAdjustment.amount) || 0;
    const pct = parseFloat(newAdjustment.percentage) || 0;
    if (newAdjustment.adjustment_type === 'amount' && amt <= 0) return;
    if (newAdjustment.adjustment_type === 'percentage' && pct <= 0) return;
    setAdjustments((prev) => [
      ...prev,
      {
        id:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `adj-${Date.now()}`,
        operation: newAdjustment.operation,
        adjustment_type: newAdjustment.adjustment_type,
        amount: amt,
        percentage: pct,
        reason: newAdjustment.reason.trim(),
      },
    ]);
    setNewAdjustment({
      operation: 'add',
      adjustment_type: 'amount',
      amount: '',
      percentage: '',
      reason: '',
      show: false,
    });
  };

  const removeAdjustment = (id: string) => {
    setAdjustments((prev) => prev.filter((a) => a.id !== id));
  };

  const fmt = (n: number) =>
    `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  // ── Step panels ────────────────────────────────────────────────────────

  const eventDetailsPanel = (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Event Details</h3>
        <p className="text-sm text-muted-foreground">
          Configure the event type, scoring, fees, and performance assignment.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>
            Event Type <span className="text-destructive">*</span>
          </Label>
          {(() => {
            // Rodeo-wide uniqueness — the new schema has a single config per
            // event_type per rodeo, so an event type consumed by a sibling
            // event is off-limits everywhere in this rodeo.
            const usedSet = new Set(
              (usedEventTypes ?? []).map((t) => t.toLowerCase().trim()),
            );
            const currentLower = type.toLowerCase().trim();
            return (
              <Select
                value={type}
                onValueChange={(v) => {
                  setType(v);
                  const derived = findRodeoEventType(v)?.category;
                  if (derived) setCategory(derived);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select or type" />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_EVENT_TYPES.map((t) => {
                    const key = t.toLowerCase().trim();
                    const isUsed = usedSet.has(key) && key !== currentLower;
                    return (
                      <SelectItem key={t} value={t} disabled={isUsed}>
                        {t}
                        {isUsed && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                            Already in this rodeo
                          </span>
                        )}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            );
          })()}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="score_type">Score Type</Label>
          <Input
            id="score_type"
            value={
              findRodeoEventType(type)?.category === 'scored'
                ? 'Scored (highest wins)'
                : findRodeoEventType(type)?.category === 'timed'
                  ? 'Timed (lowest wins)'
                  : ''
            }
            readOnly
            tabIndex={-1}
            placeholder="Select an event type"
            className="bg-muted cursor-default"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="event_name">
            Event Name{' '}
            <span className="text-muted-foreground text-xs">(optional)</span>
          </Label>
          <Input
            id="event_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={type || 'Defaults to event type'}
          />
          <p className="text-xs text-muted-foreground">
            Leave blank to use the event type as the title.
          </p>
        </div>

        {/* Per-event judge-sheet template override. The empty sentinel
            "__auto__" maps to NULL on submit so the BEFORE-INSERT trigger
            (see migration 20260602100000) can fill in the auto-classified
            system template. */}
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="judge_sheet_template_id">Judge's Sheet Template</Label>
          <Select
            value={judgeSheetTemplateId ?? '__auto__'}
            onValueChange={(v) =>
              setJudgeSheetTemplateId(v === '__auto__' ? null : v)
            }
          >
            <SelectTrigger id="judge_sheet_template_id" className="w-full">
              <SelectValue placeholder="Auto (by event type)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__auto__">Auto (by event type)</SelectItem>
              {(judgeSheetTemplates ?? []).map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.display_name}
                  {t.is_system ? ' · System' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Controls the column layout printed on this event's judge sheet.
            Manage templates in Settings → System Configuration.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Entry Fee</Label>
          <CurrencyInput value={entryFee} onChange={setEntryFee} />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 min-h-[20px]">
            <Label htmlFor="transaction_fee">Transaction Fee</Label>
            {isAllOrgAdmin &&
              (editTxnFee ? (
                <button
                  type="button"
                  onClick={() => {
                    setEditTxnFee(false);
                    // Clearing regFee restores the Stripe-derived fee — the
                    // save handler emits null and the backend recomputes.
                    setRegFee('');
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  title="Cancel override and use the Stripe-calculated fee"
                >
                  <X className="h-3 w-3" />
                  Reset to auto
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditTxnFee(true)}
                  className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="Override transaction fee (super admin)"
                  aria-label="Override transaction fee"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              ))}
          </div>
          {editTxnFee && isAllOrgAdmin ? (
            <CurrencyInput
              id="transaction_fee"
              value={regFee}
              onChange={setRegFee}
              placeholder={formatCurrency(autoTxnFee).replace('$', '')}
            />
          ) : (
            <Input
              id="transaction_fee"
              value={formatCurrency(displayedTxnFee)}
              readOnly
              tabIndex={-1}
              className="bg-muted cursor-default"
            />
          )}
          <p className="text-xs text-muted-foreground">
            {hasManualOverride && !editTxnFee
              ? 'Manual override — clears to Stripe-derived if reset.'
              : stripeFeeConfig?.enabled === false
                ? 'Stripe fees disabled — no transaction fee will be charged.'
                : stripeFeeConfig
                  ? `Auto-calculated from Stripe (${(stripeFeeConfig.percentage_rate * 100)
                      .toFixed(2)
                      .replace(/\.?0+$/, '')}% + ${formatCurrency(
                      stripeFeeConfig.fixed_fee_cents / 100,
                    )}).`
                  : 'Loading fee config…'}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Contestant Limit</Label>
          <Input
            type="number"
            min={1}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="Unlimited"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Contestants per Performance</Label>
          <Input
            type="number"
            min={1}
            value={perPerfLimit}
            onChange={(e) => setPerPerfLimit(e.target.value)}
            placeholder="Auto (split contestant limit)"
          />
          <p className="text-xs text-muted-foreground">
            How many contestants run in each performance. Slack is uncapped — any overflow rolls into slack.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Non-Member Surcharge</Label>
          <CurrencyInput
            value={surcharge}
            onChange={setSurcharge}
            placeholder="Use rodeo default"
          />
        </div>
        {/* Per-event Pay Later override. Defaults to inheriting the rodeo's
            switch; an explicit choice wins in either direction, so a single
            event can opt out of (or into) Pay Later on its own. */}
        <div className="space-y-1.5">
          <Label htmlFor="event-allow-pay-later">Pay Later</Label>
          <Select
            value={payLaterOverride}
            onValueChange={(v) =>
              setPayLaterOverride(v as 'inherit' | 'allow' | 'disallow')
            }
          >
            <SelectTrigger id="event-allow-pay-later">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="inherit">Use rodeo setting</SelectItem>
              <SelectItem value="allow">Allow for this event</SelectItem>
              <SelectItem value="disallow">Not for this event</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Overrides the rodeo's Pay Later switch for this event only. Still
            members-only when the rodeo requires membership.
          </p>
        </div>
        {/* Multi-registration cap. Off by default — most events allow a
            contestant exactly one entry. When toggled on, admins set how
            many entries the same contestant may hold; the backend rejects
            further submits with MAX_REGISTRATIONS_REACHED once the cap
            is hit. */}
        <div className="space-y-1.5 sm:col-span-2">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={allowMultiReg}
              onChange={(e) => setAllowMultiReg(e.target.checked)}
              className="h-4 w-4 mt-0.5 rounded border-gray-300"
            />
            <span>
              Allow same contestant to enter multiple times
              <span className="block text-xs font-normal text-muted-foreground">
                Off by default — one entry per contestant. Turn on for
                events where a rider can enter twice or more (common for
                jackpots).
              </span>
            </span>
          </label>
          {allowMultiReg && (
            <div className="ml-6 space-y-1.5">
              <Label htmlFor="max_per_user">
                Max entries per contestant
              </Label>
              <Input
                id="max_per_user"
                type="number"
                min={1}
                max={10}
                value={maxPerUser}
                onChange={(e) => setMaxPerUser(e.target.value)}
                placeholder="1"
                className="max-w-[160px]"
              />
            </div>
          )}
          {/* Balanced Header/Heeler split — team roping only, and only once
              the cap allows a second entry (the backend ignores it
              otherwise). */}
          {allowMultiReg &&
            (parseInt(maxPerUser, 10) || 1) > 1 &&
            isTeamRopingEvent({ trig_event_type_name: type }) && (
              <label className="ml-6 flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={balancedPositions}
                  onChange={(e) => setBalancedPositions(e.target.checked)}
                  className="h-4 w-4 mt-0.5 rounded border-gray-300"
                />
                <span>
                  Require an even Header / Heeler split
                  <span className="block text-xs font-normal text-muted-foreground">
                    Entries must be spread across both ends —{' '}
                    {describeBalancedSplit(parseInt(maxPerUser, 10) || 1)}.
                    Applies to new entries only; existing ones are left alone.
                  </span>
                </span>
              </label>
            )}
        </div>
        {performances && (
          <div className="space-y-2 sm:col-span-2">
            <Label>Performances</Label>
            <p className="text-xs text-muted-foreground">
              Pick the performances this event runs in. An event can run in
              multiple performances — the fastest time across all of them
              wins. Contestant limit defaults to the event's rodeo-level cap;
              set a per-performance override if that perf caps differently.
            </p>
            {performances.length === 0 ? (
              <div className="rounded-md border border-dashed border-amber-500/60 bg-amber-500/10 p-3">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  No performances added yet. Finish this event and move on to
                  the Performances step — you can assign this event to a
                  perf from there, or come back here after perfs exist.
                </p>
              </div>
            ) : (
            <div className="space-y-1.5 rounded-md border p-2 bg-muted/20">
              {[...performances]
                .sort(byPerformanceStart)
                .map((p) => {
                const assignment = assignments.find(
                  (a) => a.performance_key === p.key,
                );
                const checked = !!assignment;
                const toggle = (on: boolean) => {
                  setAssignments((prev) =>
                    on
                      ? [
                          ...prev.filter((a) => a.performance_key !== p.key),
                          { performance_key: p.key, max_contestants: null },
                        ]
                      : prev.filter((a) => a.performance_key !== p.key),
                  );
                  // Dropping a perf from the event must drop it from any round
                  // that was tied to it — otherwise the round would save a
                  // performance_id the event no longer runs in.
                  if (!on) {
                    setRounds((prev) =>
                      prev.map((r) =>
                        r.performance_key === p.key
                          ? { ...r, performance_key: null }
                          : r,
                      ),
                    );
                  }
                };
                const setOverride = (raw: string) => {
                  const n = raw ? parseInt(raw, 10) : NaN;
                  setAssignments((prev) =>
                    prev.map((a) =>
                      a.performance_key === p.key
                        ? {
                            ...a,
                            max_contestants: Number.isFinite(n) && n >= 1 ? n : null,
                          }
                        : a,
                    ),
                  );
                };
                // Effective cap when the user hasn't set a per-perf override:
                // perf default wins over event-level limit wins over unlimited.
                // Exposing this as the placeholder lets the user see what the
                // cap actually is without having to retype a value.
                const effectiveFallback =
                  p.default_max_contestants ??
                  (limit ? parseInt(limit, 10) : null);
                const capPlaceholder =
                  effectiveFallback != null
                    ? String(effectiveFallback)
                    : 'unlimited';
                // Build the "Fri May 7 · 7:00 PM" subtitle from the perf's
                // date + start_time. Either piece alone is still useful.
                const dateLabel = formatDate(p.performance_date);
                const timeLabel = formatTime(p.start_time);
                const subtitle = [dateLabel, timeLabel]
                  .filter(Boolean)
                  .join(' · ');
                const otherEvents = p.assigned_event_types ?? [];
                // The user has to pick a discipline first — assigning a perf
                // without an event_type would create a junction row pointing
                // at nothing meaningful.
                const noEventTypeYet = type.trim().length === 0;
                // Performances are now multi-event: a perf can host any number
                // of disciplines. Other events claiming the perf are still
                // shown as chips for context, but they no longer lock the
                // toggle — the user must be free to add or remove this event
                // from any perf in either add or edit mode.
                const locked = noEventTypeYet;
                const lockTitle = noEventTypeYet
                  ? 'Select an event type first'
                  : undefined;
                // Echo the current event type into the chip row when the
                // toggle is on so the user gets a visual confirmation that
                // their event is now part of this perf — the parent omits
                // the editing event from `assigned_event_types` to avoid a
                // self-conflict warning, which left the row reading as if
                // nothing was assigned.
                const ownChipLabel = checked
                  ? (type.trim() || name.trim() || null)
                  : null;
                return (
                  <div
                    key={p.key}
                    className={`flex items-center gap-3 px-2 py-1.5 rounded-md transition-colors ${
                      locked ? 'opacity-60' : 'hover:bg-muted/50'
                    }`}
                    title={lockTitle}
                  >
                    <Switch
                      checked={checked}
                      onCheckedChange={toggle}
                      disabled={locked}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm">{p.name}</span>
                        {ownChipLabel && (
                          <Badge
                            variant="outline"
                            className="text-[10px] text-emerald-700 border-emerald-400 bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-600"
                          >
                            {ownChipLabel}
                          </Badge>
                        )}
                        {otherEvents.map((ev) => (
                          <Badge
                            key={ev}
                            variant="outline"
                            className="text-[10px] text-blue-600 border-blue-300"
                          >
                            {ev}
                          </Badge>
                        ))}
                      </div>
                      {subtitle && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {subtitle}
                        </p>
                      )}
                    </div>
                    {checked && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          cap
                        </span>
                        <Input
                          type="number"
                          min={1}
                          value={
                            assignment?.max_contestants != null
                              ? String(assignment.max_contestants)
                              : ''
                          }
                          onChange={(e) => setOverride(e.target.value)}
                          placeholder={capPlaceholder}
                          title={
                            p.default_max_contestants != null
                              ? `Performance default: ${p.default_max_contestants}. Leave blank to inherit.`
                              : 'Leave blank to inherit the event-level limit.'
                          }
                          className="h-7 w-28 text-xs"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Every performance on the rodeo, chronological. A round may be tied to any
  // of them: picking one the event isn't on yet ALSO assigns the event to that
  // performance (see tieRoundToPerformance), so the id we persist is always a
  // real assignment. A finals rodeo depends on this — Round 2 → Saturday must
  // put the event on Saturday's performance for the draw and scoring to run.
  const allPerformances = [...(performances ?? [])].sort(byPerformanceStart);
  const isAssigned = (key: string) =>
    assignments.some((a) => a.performance_key === key);
  const tieRoundToPerformance = (idx: number, key: string | null) => {
    updateRound(idx, { performance_key: key });
    if (key && !isAssigned(key)) {
      setAssignments((prev) => [
        ...prev.filter((a) => a.performance_key !== key),
        { performance_key: key, max_contestants: null },
      ]);
    }
  };

  /** Display name for a round's tied performance; null when unresolvable. */
  const performanceName = (key: string | null | undefined) =>
    key ? (performances?.find((p) => p.key === key)?.name ?? null) : null;

  const roundsPanel = (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Rounds</h3>
          <p className="text-sm text-muted-foreground">
            Configure each round for this event.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={addRound}>
          <Plus className="h-4 w-4 mr-1" /> Add Round
        </Button>
      </div>

      {rounds.filter((r) => !r.is_average).length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-6 text-center">
          No rounds yet. Add a round to configure go/short rounds and
          advancing counts.
        </p>
      ) : (
        <div className="space-y-2">
          {rounds.map((r, idx) => {
            // AVG is a scoring-only construct — keep it in state so it
            // survives a round-trip but never render it in the setup UI.
            if (r.is_average) return null;
            // Visible round position (1-based), counting non-AVG rows only
            // so the "Round 1" floor and labels stay consistent even when
            // legacy AVG rows live alongside.
            const visiblePos =
              rounds.slice(0, idx).filter((x) => !x.is_average).length + 1;
            // Name of the perf this round is tied to. Null while the round is
            // on "Default" — the header then stays quiet rather than echoing
            // the fallback as if the user had chosen it.
            const tiedPerfName = performanceName(r.performance_key);
            return (
            <div
              key={idx}
              className="border rounded-md p-3 space-y-2 bg-muted/20"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-muted-foreground">
                  {`Round ${visiblePos}`}
                  {tiedPerfName && (
                    <span className="ml-1.5 text-muted-foreground/80">
                      · {tiedPerfName}
                    </span>
                  )}
                </p>
                {/*
                  Round 1 is the setup floor — every event must keep at
                  least one editable round, so the first visible row
                  hides its remove button. All later rounds remain
                  removable.
                */}
                {visiblePos > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => removeRound(idx)}
                    aria-label="Remove round"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Round Name</Label>
                  <Input
                    value={r.round_name}
                    onChange={(e) =>
                      updateRound(idx, { round_name: e.target.value })
                    }
                    placeholder={`Round ${visiblePos}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Advancing Count</Label>
                  <Input
                    type="number"
                    min={1}
                    value={r.advancing_count ?? ''}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      updateRound(idx, {
                        advancing_count: Number.isNaN(n) ? null : n,
                      });
                    }}
                    placeholder="All"
                  />
                </div>
                {/*
                  Per-round performance. Offers every performance on the rodeo;
                  choosing one the event isn't assigned to yet assigns it, so a
                  round can never point at a perf the event doesn't run in.
                  Hidden for the Avg row, which is a payout aggregate rather
                  than a run.
                */}
                {allPerformances.length > 0 && (
                  <div className="space-y-1">
                    <Label className="text-xs">Performance</Label>
                    <Select
                      value={r.performance_key ?? DEFAULT_PERF_VALUE}
                      onValueChange={(v) =>
                        tieRoundToPerformance(idx, v === DEFAULT_PERF_VALUE ? null : v)
                      }
                    >
                      <SelectTrigger aria-label={`Round ${visiblePos} performance`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={DEFAULT_PERF_VALUE}>
                          Default (event's first performance)
                        </SelectItem>
                        {allPerformances.map((p) => (
                          <SelectItem key={p.key} value={p.key}>
                            {p.name}
                            {!isAssigned(p.key) && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (adds this event to it)
                              </span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Picking a performance the event isn't on yet also assigns the
                      event to that performance.
                    </p>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={r.is_go_round}
                    onCheckedChange={(v) =>
                      updateRound(idx, { is_go_round: v })
                    }
                  />
                  Go round
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={r.is_short_round}
                    onCheckedChange={(v) =>
                      updateRound(idx, { is_short_round: v })
                    }
                  />
                  Short round
                </label>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const prizesPanel = (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold">Prize Management</h3>
        <p className="text-sm text-muted-foreground">
          Configure the prize pool and per-round allocation.
        </p>
      </div>

      {/* Prize allocation mode: fixed pool vs. dynamic (per-entry) */}
      <div className="rounded-lg border border-border p-4 space-y-4">
        <div>
          <Label>Prize Allocation</Label>
          <div className="mt-1.5 inline-flex rounded-md border border-input overflow-hidden">
            <button
              type="button"
              onClick={() => setPrizeMode('fixed')}
              className={`px-3 py-1.5 text-sm transition-colors ${
                prizeMode === 'fixed'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-accent'
              }`}
            >
              Fixed amount
            </button>
            <button
              type="button"
              onClick={() => setPrizeMode('dynamic')}
              className={`px-3 py-1.5 text-sm transition-colors border-l border-input ${
                prizeMode === 'dynamic'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-accent'
              }`}
            >
              Dynamic (per entry)
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            {prizeMode === 'fixed'
              ? 'The prize pool is the fixed Total Prize Amount below.'
              : 'The prize pool grows with entries: prize per entry × number of contestant entries.'}
          </p>
        </div>

        {/* Per-entry contribution — only relevant for dynamic prizes (a fixed
            prize is a flat pool regardless of entries). Three modes: a flat $
            amount, a % of the entry fee, or the entry fee MINUS a fixed amount
            the org keeps. Pre-filled from the organization default for new
            events. */}
        {prizeMode === 'dynamic' && (
          <div className="space-y-1.5">
            <Label>
              {perEntryMode === 'fee_deduction'
                ? 'Amount kept from each entry fee'
                : 'Amount added to prize per entry'}
            </Label>
            <div className="inline-flex rounded-md border border-input overflow-hidden">
              <button
                type="button"
                onClick={() => setPerEntryMode('amount')}
                className={`px-2.5 py-1.5 text-sm transition-colors ${
                  perEntryMode === 'amount'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-accent'
                }`}
              >
                $ flat
              </button>
              <button
                type="button"
                onClick={() => setPerEntryMode('percentage')}
                className={`px-2.5 py-1.5 text-sm border-l border-input transition-colors ${
                  perEntryMode === 'percentage'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-accent'
                }`}
              >
                % of fee
              </button>
              <button
                type="button"
                onClick={() => setPerEntryMode('fee_deduction')}
                className={`px-2.5 py-1.5 text-sm border-l border-input transition-colors ${
                  perEntryMode === 'fee_deduction'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-accent'
                }`}
              >
                Fee − $
              </button>
            </div>
            <div className="flex items-center gap-2">
              {perEntryMode === 'percentage' ? (
                <div className="flex items-center gap-1 flex-1">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={perEntry}
                    onChange={(e) => {
                      // Can't take more than 100% of the entry fee.
                      const n = parseFloat(e.target.value);
                      setPerEntry(!Number.isNaN(n) && n > 100 ? '100' : e.target.value);
                    }}
                    placeholder="0"
                  />
                  <span className="text-sm text-muted-foreground">% of entry fee</span>
                </div>
              ) : (
                <div className="flex-1">
                  <CurrencyInput
                    value={perEntry}
                    onChange={(v) => {
                      // Flat amount can't exceed the entry fee; a deduction can't
                      // exceed it either (that would just zero out the prize).
                      const n = parseFloat(v);
                      setPerEntry(
                        !Number.isNaN(n) && entryFeeNum > 0 && n > entryFeeNum
                          ? String(entryFeeNum)
                          : v,
                      );
                    }}
                    placeholder="0"
                  />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {perEntryMode === 'percentage' &&
                'Added to the prize pool for each contestant entry (a percentage of the entry fee — max 100%).'}
              {perEntryMode === 'amount' &&
                `Added to the prize pool for each contestant entry${
                  entryFeeNum > 0 ? ` (max ${fmt(entryFeeNum)} — the entry fee).` : '.'
                }`}
              {perEntryMode === 'fee_deduction' &&
                (entryFeeNum > 0
                  ? `The whole entry fee goes to the prize except this amount the org keeps: ${fmt(
                      entryFeeNum,
                    )} − ${fmt(Math.min(parseFloat(perEntry) || 0, entryFeeNum))} = ${fmt(
                      Math.max(0, entryFeeNum - (parseFloat(perEntry) || 0)),
                    )} added to the prize per entry.`
                  : 'The whole entry fee goes to the prize except this fixed amount the org keeps from each entry.')}
            </p>
          </div>
        )}

        <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
          <div className="min-w-0">
            <Label htmlFor="display_prize_to_contestants">Show prize to contestants</Label>
            <p className="text-xs text-muted-foreground mt-1">
              When on, contestants see this event's prize{prizeMode === 'dynamic' ? ', calculated live as entries come in,' : ''} and it rolls into the rodeo prize totals.
            </p>
          </div>
          <Switch
            id="display_prize_to_contestants"
            checked={displayPrize}
            onCheckedChange={setDisplayPrize}
            className="mt-1 shrink-0"
          />
        </div>

        <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
          <div className="min-w-0">
            <Label htmlFor="show_prize_amounts_in_results">Show prize amounts in results</Label>
            <p className="text-xs text-muted-foreground mt-1">
              When on, prize amounts appear next to placed contestants in the results once the event is fully scored. Turn off to hide prize allocations from the results entirely.
            </p>
          </div>
          <Switch
            id="show_prize_amounts_in_results"
            checked={showPrizeInResults}
            onCheckedChange={setShowPrizeInResults}
            className="mt-1 shrink-0"
          />
        </div>

        <div
          className={`flex items-start justify-between gap-4 rounded-md border border-border p-3${
            showPrizeInResults ? '' : ' opacity-50'
          }`}
        >
          <div className="min-w-0">
            <Label htmlFor="show_prize_amounts_before_complete">Show prize amounts before all contestants compete</Label>
            <p className="text-xs text-muted-foreground mt-1">
              Off by default: prize amounts stay hidden in the results until every contestant has performed and been scored, so no one sees payouts before they compete. Turn on to reveal the running amounts live as the event runs.
            </p>
          </div>
          <Switch
            id="show_prize_amounts_before_complete"
            checked={showPrizeInResults && showPrizeBeforeComplete}
            onCheckedChange={setShowPrizeBeforeComplete}
            disabled={!showPrizeInResults}
            className="mt-1 shrink-0"
          />
        </div>

        {/* Info tip: dynamic prize + a percentage adjustment. Required by spec
            so organizers understand the order of operations. */}
        {prizeMode === 'dynamic' &&
          adjustments.some((a) => a.adjustment_type === 'percentage') && (
            <div className="rounded-md border border-amber-400/60 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-600/60 p-3 text-xs text-amber-800 dark:text-amber-300">
              <span className="font-semibold">How percentage adjustments apply: </span>
              dollar adjustments are applied first (the per-entry pool plus any fixed additions,
              minus any fixed deductions), then each percentage is taken on that running subtotal.
            </div>
          )}
      </div>

      {/* Total Prize Amount (fixed mode only) + Available Prize summary. A
          dynamic prize has no flat Total Prize Amount — it's built from the
          per-entry contribution and the adjustments below. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {prizeMode === 'fixed' && (
          <div className="space-y-1.5">
            <Label>Total Prize Amount</Label>
            <CurrencyInput
              value={totalPrize}
              onChange={setTotalPrize}
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground">
              Base prize amount for this event (before adjustments)
            </p>
          </div>
        )}

        <div className="rounded-lg border-2 border-dashed border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-600 p-4 flex flex-col justify-center">
          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
            Available Prize Amount
          </p>
          <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-300">
            {fmt(availablePrize)}
          </p>
          <p className="text-xs">
            <span className="text-emerald-600 dark:text-emerald-500">
              Base: {fmt(prizeBase)}
            </span>
            {' | '}
            <span
              className={
                totalAdjustments >= 0
                  ? 'text-emerald-600 dark:text-emerald-500'
                  : 'text-red-600 dark:text-red-400'
              }
            >
              Adjustments: {totalAdjustments >= 0 ? '+' : ''}
              {fmt(totalAdjustments)}
            </span>
          </p>
        </div>
      </div>

      {/* Prize Adjustments */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">Prize Adjustments</h4>
          {!newAdjustment.show && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setNewAdjustment((p) => ({ ...p, show: true }))}
              className="h-7 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Adjustment
            </Button>
          )}
        </div>

        {/* Inherited organization default adjustments. Applied automatically to
            every event in the org's rodeos. Toggle on/off and override the
            amount per event; wording is locked and they can't be deleted. */}
        {(orgDefaults?.length ?? 0) > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              Organization defaults · on by default, cannot be deleted
            </p>
            {orgDefaults!.map((d) => {
              const ov = defaultOverrides[d.id] ?? {};
              const isPct = d.percentage !== null && d.percentage !== undefined;
              const active = ov.is_active ?? d.is_active;
              const amountVal = ov.amount ?? d.amount;
              const pctVal = ov.percentage ?? d.percentage ?? 0;
              const setOverride = (patch: {
                is_active?: boolean;
                amount?: number | null;
                percentage?: number | null;
              }) =>
                setDefaultOverrides((prev) => ({
                  ...prev,
                  [d.id]: { ...prev[d.id], ...patch },
                }));
              return (
                <div
                  key={d.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border p-2.5"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {d.adjustment_type === 'addition' ? '+ ' : '− '}
                      {d.reason}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {isPct ? 'Percentage' : 'Fixed amount'} · from organization
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isPct ? (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={String(pctVal)}
                          onChange={(e) =>
                            setOverride({ percentage: e.target.value === '' ? 0 : Number(e.target.value) })
                          }
                          className="h-8 w-20 text-sm"
                          disabled={!active}
                        />
                        <span className="text-xs text-muted-foreground">%</span>
                      </div>
                    ) : (
                      <div className="w-28">
                        <CurrencyInput
                          value={Number(amountVal).toFixed(2)}
                          onChange={(v) =>
                            setOverride({ amount: v === '' ? 0 : parseFloat(v) || 0 })
                          }
                          className="h-8 text-sm"
                        />
                      </div>
                    )}
                    <Switch
                      checked={active}
                      onCheckedChange={(v) => setOverride({ is_active: v })}
                      aria-label={`Toggle ${d.reason}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {newAdjustment.show && (
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Operation</Label>
                <Select
                  value={newAdjustment.operation}
                  onValueChange={(v) =>
                    setNewAdjustment((p) => ({
                      ...p,
                      operation: v as 'add' | 'subtract',
                    }))
                  }
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">Add Money (+)</SelectItem>
                    <SelectItem value="subtract">Subtract Money (-)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Adjustment Type</Label>
                <Select
                  value={newAdjustment.adjustment_type}
                  onValueChange={(v) =>
                    setNewAdjustment((p) => ({
                      ...p,
                      adjustment_type: v as 'amount' | 'percentage',
                    }))
                  }
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amount">Fixed Amount</SelectItem>
                    <SelectItem value="percentage">Percentage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">
                  {newAdjustment.adjustment_type === 'amount' ? 'Amount' : 'Percentage'}
                </Label>
                {newAdjustment.adjustment_type === 'amount' ? (
                  <CurrencyInput
                    value={newAdjustment.amount}
                    onChange={(v) =>
                      setNewAdjustment((p) => ({ ...p, amount: v }))
                    }
                    className="h-9 text-sm"
                  />
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                      %
                    </span>
                    <Input
                      type="number"
                      min={0}
                      step={0.1}
                      value={newAdjustment.percentage}
                      onChange={(e) =>
                        setNewAdjustment((p) => ({
                          ...p,
                          percentage: e.target.value,
                        }))
                      }
                      placeholder="0"
                      className="pl-7 h-9 text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                Description <span className="text-destructive">*</span>
              </Label>
              <Input
                value={newAdjustment.reason}
                onChange={(e) =>
                  setNewAdjustment((p) => ({ ...p, reason: e.target.value }))
                }
                placeholder="e.g. Sponsor contribution, Arena fees..."
                className="h-9 text-sm"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  setNewAdjustment({
                    operation: 'add',
                    adjustment_type: 'amount',
                    amount: '',
                    percentage: '',
                    reason: '',
                    show: false,
                  })
                }
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={saveAdjustment}
                disabled={!newAdjustment.reason.trim()}
              >
                Save Adjustment
              </Button>
            </div>
          </div>
        )}

        {adjustments.length > 0 ? (
          <div className="space-y-1.5">
            {adjustments.map((adj) => {
              const isAdd = adj.operation === 'add';
              const displayValue =
                adj.adjustment_type === 'amount'
                  ? fmt(adj.amount)
                  : `${adj.percentage}%`;
              return (
                <div
                  key={adj.id}
                  className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm ${
                    isAdd
                      ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20'
                      : 'border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-mono font-semibold ${
                        isAdd
                          ? 'text-emerald-700 dark:text-emerald-400'
                          : 'text-red-700 dark:text-red-400'
                      }`}
                    >
                      {isAdd ? '+' : '-'}
                      {displayValue}
                    </span>
                    <span className="text-muted-foreground">{adj.reason}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAdjustment(adj.id)}
                    className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                    aria-label="Remove adjustment"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          !newAdjustment.show && (
            <p className="text-xs text-muted-foreground italic">
              No prize adjustments added
            </p>
          )
        )}
      </div>

      {/* Per-round prize allocation */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold">Per-Round Prize Allocation</h4>

        {rounds.filter((r) => !r.is_average).length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-muted-foreground/25 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              No rounds configured yet. Add rounds in step 2 to allocate
              prizes per round.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {rounds.map((r, idx) => {
              // AVG is scoring-only — keep it in state for round-trip but
              // never offer it as a prize-allocation target in setup.
              if (r.is_average) return null;
              const visiblePos =
                rounds.slice(0, idx).filter((x) => !x.is_average).length + 1;
              return (
              <div
                key={idx}
                className="rounded-md border bg-muted/20 p-3 space-y-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={r.has_prizes}
                      onCheckedChange={(v) => toggleRoundPrize(idx, v)}
                    />
                    <div>
                      <p className="text-sm font-medium">
                        {r.round_name || `Round ${visiblePos}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {r.has_prizes
                          ? 'Prizes enabled for this round'
                          : 'No prizes for this round'}
                      </p>
                    </div>
                  </div>
                </div>
                {r.has_prizes && (
                  <RodeoPrizePlacesEditor
                    round={r}
                    onChange={(patch) => updateRound(idx, patch)}
                    prizeTotalReadOnly={prizeMode === 'dynamic'}
                  />
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Average (All Rounds) payout — the overall best score/time across every
          round. Not a real round; a standalone payout from the same pool. */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold">Average (All Rounds)</h4>
        <div className="rounded-md border bg-muted/20 p-3 space-y-2">
          <div className="flex items-center gap-3">
            <Switch
              checked={!!avgRound?.has_prizes}
              onCheckedChange={toggleAvgPrize}
            />
            <div>
              <p className="text-sm font-medium">Average (All Rounds)</p>
              <p className="text-xs text-muted-foreground">
                {avgRound?.has_prizes
                  ? 'Pays the overall best across all rounds'
                  : 'No average payout for this event'}
              </p>
            </div>
          </div>
          {avgRound?.has_prizes && (
            <RodeoPrizePlacesEditor
              round={avgRound}
              onChange={updateAvgRound}
              hideProgressive
              prizeTotalReadOnly={prizeMode === 'dynamic'}
            />
          )}
        </div>
      </div>

      {/* Summary */}
      {(() => {
        const isOver = remainingPrize < 0;
        const isUnder =
          remainingPrize > 0 && rounds.some((r) => r.has_prizes);
        let border = 'border-teal-400 dark:border-teal-600';
        let bg = 'bg-teal-50 dark:bg-teal-950/30';
        let title = 'text-teal-800 dark:text-teal-300';
        let sub = 'text-teal-600 dark:text-teal-400';
        if (isOver) {
          border = 'border-red-400 dark:border-red-600';
          bg = 'bg-red-50 dark:bg-red-950/30';
          title = 'text-red-800 dark:text-red-300';
          sub = 'text-red-600 dark:text-red-400';
        } else if (isUnder) {
          border = 'border-amber-400 dark:border-amber-600';
          bg = 'bg-amber-50 dark:bg-amber-950/30';
          title = 'text-amber-800 dark:text-amber-300';
          sub = 'text-amber-600 dark:text-amber-400';
        }
        return (
          <div
            className={`rounded-lg border-2 border-dashed ${border} ${bg} px-4 py-3 flex items-center justify-between`}
          >
            <div>
              <p className={`text-sm font-semibold ${title}`}>
                Total Prize Rounds Allocated:
              </p>
              <p className={`text-xs ${sub}`}>
                {rounds.filter((r) => r.has_prizes).length}{' '}
                prize
                {rounds.filter((r) => r.has_prizes).length ===
                1
                  ? ''
                  : 's'}{' '}
                configured — {fmt(availablePrize)} available for allocation
              </p>
            </div>
            <div className="text-right">
              <p className={`text-lg font-bold ${title}`}>
                {fmt(totalAllocated)}
              </p>
              <p className={`text-xs ${sub}`}>
                ({isOver ? `-${fmt(Math.abs(remainingPrize))}` : fmt(remainingPrize)} remaining)
              </p>
            </div>
          </div>
        );
      })()}
    </div>
  );

  const allStepContent = showRounds
    ? [eventDetailsPanel, roundsPanel, prizesPanel]
    : [eventDetailsPanel];

  // ── Footer actions ─────────────────────────────────────────────────────

  const primaryAction = (
    <Button onClick={handleSubmit} disabled={!canSubmit}>
      {isSaving
        ? 'Saving...'
        : mode === 'edit'
          ? 'Save Changes'
          : 'Add Event'}
    </Button>
  );

  // WizardShell's auto Cancel is fine, but we want to match the rest of the
  // rodeo dialogs by wiring it to onOpenChange explicitly.
  const cancelButton = (
    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
      Cancel
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="sm:max-w-[900px] p-0 gap-0 h-[85vh] flex flex-col overflow-hidden"
      >
        <DialogTitle className="sr-only">
          {mode === 'edit' ? 'Edit Event' : 'Add Event'}
        </DialogTitle>

        {/* Header — mirrors WizardShell's own chrome but rendered here so the
            Dialog owns the X close button and WizardShell keeps only the
            body/nav/footer. */}
        <div
          className="border-b px-6 py-4 shrink-0"
          style={{
            background: 'hsl(var(--sidebar-background))',
            color: 'hsl(var(--sidebar-foreground))',
          }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">
              {mode === 'edit' ? 'Edit Event' : 'Add Event'}
            </h2>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              className="p-2 rounded-lg transition-colors opacity-70 hover:opacity-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body + footer via WizardShell. No title prop so it doesn't render
            its own header alongside the dialog header above. */}
        <WizardShell
          steps={steps}
          currentStep={step}
          onStepChange={setStep}
          onNext={() => setStep((s) => Math.min(steps.length, s + 1))}
          onBack={() => setStep((s) => Math.max(1, s - 1))}
          invalidSteps={missingSteps}
          allStepContent={allStepContent}
          cancelButton={cancelButton}
          primaryAction={primaryAction}
          belowContent={
            errorMessage ? (
              <p className="text-sm text-destructive" role="alert">
                {errorMessage}
              </p>
            ) : null
          }
          className="flex-1 min-h-0"
        >
          {allStepContent[step - 1]}
        </WizardShell>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────── RodeoPrizePlacesEditor ──────────────────── */

interface RodeoPrizePlacesEditorProps {
  round: RodeoEventDialogRound;
  onChange: (patch: Partial<RodeoEventDialogRound>) => void;
  /** Hide the "Progressive round" toggle (e.g. for the Average payout). */
  hideProgressive?: boolean;
  /**
   * When true (dynamic prize events) the round's Prize Total is computed from
   * the live pool, so it's shown read-only — only the per-place percentages
   * are editable.
   */
  prizeTotalReadOnly?: boolean;
}

/**
 * Inline prize-place percentage editor. Mirrors the jackpot EditEventDialog
 * UX: number input + range slider per place, auto-balances to 100% by
 * borrowing/lending from the remaining places. The dollar preview to the
 * right of each row reflects the live `round_prize_total`.
 */
function RodeoPrizePlacesEditor({
  round,
  onChange,
  hideProgressive = false,
  prizeTotalReadOnly = false,
}: RodeoPrizePlacesEditorProps) {
  // Track cursor for each slot's last-adjusted pointer so incremental drags
  // feel stable (matches the jackpot dialog's rebalancing heuristic).
  const [lastAdjusted, setLastAdjusted] = React.useState<Record<number, number>>({});

  const places =
    Array.isArray(round.places) && round.places.length > 0
      ? round.places
      : [];
  const total = places.reduce((s, p) => s + (p.percentage || 0), 0);
  const isComplete = total === 100;

  const handlePlacesCountChange = (count: number) => {
    onChange({ places: createDefaultPlaces(count) });
  };

  const updatePlacePercentage = (placeIdx: number, newPct: number) => {
    const rounded = Math.max(0, Math.min(100, Math.round(newPct)));
    const next = places.map((p) => ({ ...p }));
    const currentPct = next[placeIdx].percentage;
    const diff = rounded - currentPct;
    if (diff === 0) return;

    const isLast = placeIdx === next.length - 1;

    if (isLast) {
      const aboveIdx = placeIdx - 1;
      if (aboveIdx < 0) return;
      if (diff > 0 && next[aboveIdx].percentage < diff) return;
      next[placeIdx].percentage = rounded;
      next[aboveIdx].percentage -= diff;
    } else {
      const belowCount = next.length - placeIdx - 1;
      const totalBelow = next
        .slice(placeIdx + 1)
        .reduce((s, p) => s + p.percentage, 0);
      if (diff > 0 && diff > totalBelow) return;

      next[placeIdx].percentage = rounded;

      let cursor = lastAdjusted[placeIdx] ?? belowCount - 1;
      let remaining = Math.abs(diff);
      const maxIter = remaining * belowCount + belowCount;
      let safety = 0;
      while (remaining > 0 && safety < maxIter) {
        safety++;
        const actualIdx = placeIdx + 1 + cursor;
        if (diff > 0) {
          if (next[actualIdx].percentage > 0) {
            next[actualIdx].percentage -= 1;
            remaining--;
          }
        } else {
          next[actualIdx].percentage += 1;
          remaining--;
        }
        cursor--;
        if (cursor < 0) cursor = belowCount - 1;
      }
      setLastAdjusted((prev) => ({ ...prev, [placeIdx]: cursor }));
    }

    const sum = next.reduce((s, p) => s + p.percentage, 0);
    if (sum !== 100) {
      const lastIdx = next.length - 1;
      next[lastIdx].percentage = Math.max(
        0,
        next[lastIdx].percentage + (100 - sum),
      );
    }

    onChange({ places: next });
  };

  const sliderGradient = (pct: number) => {
    const color = isComplete ? '#3b82f6' : '#f59e0b';
    return `linear-gradient(to right, ${color} 0%, ${color} ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`;
  };

  // Seed default places on first enable if the user hasn't configured any.
  React.useEffect(() => {
    if (places.length === 0) onChange({ places: createDefaultPlaces(3) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const amountForPlace = (pct: number) =>
    Math.round((Number(round.round_prize_total) || 0) * pct) / 100;

  return (
    <div className="pt-2 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
        <div className="space-y-1">
          <Label className="text-xs">Prize Total</Label>
          <CurrencyInput
            value={String(round.round_prize_total || '')}
            onChange={(v) =>
              onChange({ round_prize_total: parseFloat(v) || 0 })
            }
            disabled={prizeTotalReadOnly}
          />
          {prizeTotalReadOnly && (
            <p className="text-[11px] text-muted-foreground">
              Computed from the dynamic prize pool — set the split below.
            </p>
          )}
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Places</Label>
          <Select
            value={String(places.length || 3)}
            onValueChange={(v) => handlePlacesCountChange(Number(v))}
          >
            <SelectTrigger className="h-9 text-sm w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hideProgressive ? (
          <div />
        ) : (
          <label className="flex items-center gap-2 text-sm pb-2">
            <Switch
              checked={round.is_progressive}
              onCheckedChange={(v) => onChange({ is_progressive: v })}
            />
            Progressive round
          </label>
        )}
      </div>

      <div className="space-y-2">
        {places.map((place, idx) => (
          <div key={idx} className="rounded-md border bg-background p-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {PLACE_LABELS[idx] ?? `${idx + 1}th`} Place
              </span>
              <span className="text-sm font-semibold text-foreground">
                {round.round_prize_total > 0
                  ? `$${amountForPlace(place.percentage).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                  : `${place.percentage}%`}
              </span>
            </div>
            <div className="grid grid-cols-[80px_1fr] gap-3 items-center mt-1">
              <div className="relative">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={place.percentage}
                  onChange={(e) => {
                    const v = e.target.value === '' ? 0 : Number(e.target.value);
                    updatePlacePercentage(idx, v);
                  }}
                  className="h-8 text-xs pr-6"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  %
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={place.percentage}
                onChange={(e) => updatePlacePercentage(idx, Number(e.target.value))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-gray-400 [&::-webkit-slider-thumb]:shadow"
                style={{ background: sliderGradient(place.percentage) }}
              />
            </div>
          </div>
        ))}
      </div>

      <div
        className={`rounded px-3 py-1.5 text-xs font-medium flex justify-between ${
          isComplete
            ? 'bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-400'
            : 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-400'
        }`}
      >
        <span>Total: {total}%</span>
        {!isComplete && <span>{100 - total}% remaining</span>}
      </div>
    </div>
  );
}

export default RodeoEventDialog;
